import { useState, useEffect } from 'react';
import { DataTable } from '../../components/shared/DataTable';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { defaultVerificationTypes } from '../../data/mockData';
import { fetchProducts, fetchRates, fetchClients } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Plus,
  Package,
  IndianRupee,
  Edit,
  Trash2,
  Car,
  Upload,
  History,
  FileSpreadsheet,
  Search,
  Check,
  AlertCircle,
  Filter,
  User,
} from 'lucide-react';

export default function ProductRates() {
  const [activeTab, setActiveTab] = useState('products');
  
  // Product state management
  const [productsList, setProductsList] = useState([]);
  const [showNewProductDialog, setShowNewProductDialog] = useState(false);
  const [newProductForm, setNewProductForm] = useState({ name: '', code: '', description: '', basePrice: '' });

  const [ratesList, setRatesList] = useState([]);
  const [clientsList, setClientsList] = useState([]);

  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      try {
        const [prodRes, ratesRes, clRes] = await Promise.all([
          fetchProducts().catch(() => []),
          fetchRates().catch(() => []),
          fetchClients().catch(() => [])
        ]);
        if (isMounted) {
          if (Array.isArray(prodRes)) setProductsList(prodRes);
          if (Array.isArray(ratesRes)) {
            setRatesList(ratesRes.map((rate, idx) => ({
              ...rate,
              verificationType: rate.verificationType || defaultVerificationTypes[idx % defaultVerificationTypes.length],
              bpPayout: rate.bpPayout || Math.round((rate.vendorPayout || 0) * 0.9),
              state: rate.state || 'All States',
              city: rate.city || 'All Cities'
            })));
          }
          if (Array.isArray(clRes)) setClientsList(clRes);
        }
      } catch (err) {
        console.error('Failed to load rates/products data:', err);
      }
    }
    loadData();
    return () => { isMounted = false; };
  }, []);

  const [showNewRateDialog, setShowNewRateDialog] = useState(false);
  const [newRateForm, setNewRateForm] = useState({
    clientId: '',
    productId: '',
    verificationTypeId: '',
    state: 'All States',
    city: 'All Cities',
    clientRate: '',
    vendorPayout: '',
    fePayout: '',
    bpPayout: '',
    extraKmRate: ''
  });

  // Matrix Filter States
  const [filterProduct, setFilterProduct] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const [filterCity, setFilterCity] = useState('all');

  // Bulk Import Simulation States
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [importedFile, setImportedFile] = useState(null);
  const [importStep, setImportStep] = useState(1); // 1 = Upload, 2 = Preview & Confirm

  // Rate Adjustment History State
  const [rateHistory, setRateHistory] = useState([
    {
      id: 'HIST001',
      user: 'Rajesh Kumar',
      role: 'Admin',
      action: 'Client Rate Update',
      details: 'HDFC Bank - Address Verification client rate adjusted from ₹280 to ₹300',
      timestamp: '2026-05-20T08:30:00Z',
    },
    {
      id: 'HIST002',
      user: 'Priya Sharma',
      role: 'Supreme Manager',
      action: 'Bulk Import',
      details: 'Imported 12 new matrix rate sheets for ICICI Bank product configurations',
      timestamp: '2026-05-19T14:45:00Z',
    },
    {
      id: 'HIST003',
      user: 'Rajesh Kumar',
      role: 'Admin',
      action: 'Payout Adjustment',
      details: 'Kotak Mahindra - Business Verification: FE payout increased from ₹280 to ₹300',
      timestamp: '2026-05-18T10:15:00Z',
    }
  ]);
  const [historySearch, setHistorySearch] = useState('');

  // States & Cities for matrix configurations
  const indianStates = ['All States', 'Gujarat', 'Maharashtra', 'Delhi', 'Karnataka', 'Tamil Nadu', 'Uttar Pradesh', 'Rajasthan', 'West Bengal'];
  const citiesMap = {
    'All States': ['All Cities'],
    'Gujarat': ['All Cities', 'Ahmedabad', 'Surat', 'Vadodara', 'Rajkot'],
    'Maharashtra': ['All Cities', 'Mumbai', 'Pune', 'Nagpur', 'Thane'],
    'Delhi': ['All Cities', 'New Delhi', 'Dwarka', 'Rohini'],
    'Karnataka': ['All Cities', 'Bangalore', 'Mysore', 'Hubli'],
    'Tamil Nadu': ['All Cities', 'Chennai', 'Coimbatore', 'Madurai'],
    'Uttar Pradesh': ['All Cities', 'Noida', 'Lucknow', 'Kanpur'],
    'Rajasthan': ['All Cities', 'Jaipur', 'Jodhpur', 'Udaipur'],
    'West Bengal': ['All Cities', 'Kolkata', 'Howrah']
  };

  // Mock parsed bulk records for uploader preview
  const bulkPreviewData = [
    { client: 'HDFC Bank', product: 'Address Verification', type: 'Residence Verification', state: 'Gujarat', city: 'Ahmedabad', clientRate: 320, vendorPayout: 190, fePayout: 160, bpPayout: 145, extraKmRate: 5 },
    { client: 'ICICI Bank', product: 'KYC Verification', type: 'Aadhaar Verification', state: 'Maharashtra', city: 'Mumbai', clientRate: 260, vendorPayout: 160, fePayout: 130, bpPayout: 120, extraKmRate: 4 },
    { client: 'Kotak Mahindra', product: 'Business Verification', type: 'Business Verification', state: 'Delhi', city: 'New Delhi', clientRate: 620, vendorPayout: 400, fePayout: 320, bpPayout: 290, extraKmRate: 6 },
  ];

  // Handlers
  const handleCreateProduct = (e) => {
    e.preventDefault();
    if (!newProductForm.name || !newProductForm.code || !newProductForm.basePrice) {
      toast.error('Please fill in all required fields');
      return;
    }
    const newProduct = {
      id: `PRD00${productsList.length + 1}`,
      name: newProductForm.name,
      code: newProductForm.code.toUpperCase(),
      description: newProductForm.description || 'N/A',
      basePrice: Number(newProductForm.basePrice),
      status: 'active'
    };
    setProductsList([newProduct, ...productsList]);
    setShowNewProductDialog(false);
    setNewProductForm({ name: '', code: '', description: '', basePrice: '' });
    toast.success('Verification product added successfully!');
  };

  const handleCreateRate = (e) => {
    e.preventDefault();
    if (!newRateForm.clientId || !newRateForm.productId || !newRateForm.verificationTypeId || !newRateForm.clientRate || !newRateForm.vendorPayout || !newRateForm.fePayout || !newRateForm.bpPayout) {
      toast.error('Please configure all rates and combinations');
      return;
    }

    const clientObj = clientsList.find(c => c.id === newRateForm.clientId) || { name: 'Client' };
    const prodObj = productsList.find(p => p.id === newRateForm.productId) || { name: 'Product' };
    const typeObj = defaultVerificationTypes.find(t => t.id === newRateForm.verificationTypeId);

    const newRate = {
      id: `RATE00${ratesList.length + 1}`,
      client: clientObj,
      product: prodObj,
      verificationType: typeObj,
      state: newRateForm.state,
      city: newRateForm.city,
      clientRate: Number(newRateForm.clientRate),
      vendorPayout: Number(newRateForm.vendorPayout),
      fePayout: Number(newRateForm.fePayout),
      bpPayout: Number(newRateForm.bpPayout),
      extraKmRate: Number(newRateForm.extraKmRate || 5)
    };

    setRatesList([newRate, ...ratesList]);
    setShowNewRateDialog(false);
    
    // Add to history log
    const newLog = {
      id: `HIST00${rateHistory.length + 1}`,
      user: 'Rajesh Kumar',
      role: 'Admin',
      action: 'Rate Matrix Added',
      details: `Created new rate matrix for ${clientObj.name} - ${prodObj.name} (${newRateForm.state} / ${newRateForm.city})`,
      timestamp: new Date().toISOString()
    };
    setRateHistory([newLog, ...rateHistory]);

    setNewRateForm({
      clientId: '',
      productId: '',
      verificationTypeId: '',
      state: 'All States',
      city: 'All Cities',
      clientRate: '',
      vendorPayout: '',
      fePayout: '',
      bpPayout: '',
      extraKmRate: ''
    });
    toast.success('Matrix rate card configured successfully!');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragOver(true);
    } else if (e.type === "dragleave") {
      setDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setImportedFile(file);
      setImportStep(2);
      toast.info(`Imported sheet: ${file.name}. Reviewing rate configurations...`);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImportedFile(file);
      setImportStep(2);
      toast.info(`Selected sheet: ${file.name}. Reviewing rate configurations...`);
    }
  };

  const executeBulkImport = () => {
    // Generate rates based on preview list
    const newRates = bulkPreviewData.map((row, idx) => {
      const clientObj = clientsList.find(c => c.name === row.client) || clientsList[0] || { name: row.client };
      const prodObj = productsList.find(p => p.name === row.product) || productsList[0] || { name: row.product };
      const typeObj = defaultVerificationTypes.find(t => t.name === row.type) || defaultVerificationTypes[0];

      return {
        id: `RATE_B_${Date.now()}_${idx}`,
        client: clientObj,
        product: prodObj,
        verificationType: typeObj,
        state: row.state,
        city: row.city,
        clientRate: row.clientRate,
        vendorPayout: row.vendorPayout,
        fePayout: row.fePayout,
        bpPayout: row.bpPayout,
        extraKmRate: row.extraKmRate
      };
    });

    setRatesList([...newRates, ...ratesList]);
    setShowBulkImportDialog(false);
    setImportedFile(null);
    setImportStep(1);

    // Append to audit trail
    const newLog = {
      id: `HIST00${rateHistory.length + 1}`,
      user: 'Rajesh Kumar',
      role: 'Admin',
      action: 'Bulk Rate Import',
      details: `Imported ${bulkPreviewData.length} records successfully from ${importedFile?.name || 'RateCard_Bulk.xlsx'}`,
      timestamp: new Date().toISOString()
    };
    setRateHistory([newLog, ...rateHistory]);
    toast.success(`Success! ${bulkPreviewData.length} new rate configurations applied.`);
  };

  // Filtered Matrix Rates List
  const filteredRates = ratesList.filter(rate => {
    const matchProduct = filterProduct === 'all' || rate.product.id === filterProduct;
    const matchType = filterType === 'all' || rate.verificationType?.id === filterType;
    const matchClient = filterClient === 'all' || rate.client.id === filterClient;
    const matchState = filterState === 'all' || rate.state === filterState || rate.state === 'All States';
    const matchCity = filterCity === 'all' || rate.city === filterCity || rate.city === 'All Cities';
    return matchProduct && matchType && matchClient && matchState && matchCity;
  });

  // Filtered Audit Trail Logs
  const filteredHistory = rateHistory.filter(log => {
    const query = historySearch.toLowerCase();
    return log.user.toLowerCase().includes(query) ||
           log.action.toLowerCase().includes(query) ||
           log.details.toLowerCase().includes(query);
  });

  // Columns Definitions
  const productColumns = [
    {
      key: 'name',
      header: 'Product Name',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
            <Package className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p className="font-medium text-slate-850">{row.name}</p>
            <p className="text-xs text-slate-500">Code: {row.code}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (row) => <span className="text-sm text-slate-650">{row.description}</span>,
    },
    {
      key: 'basePrice',
      header: 'Base Price',
      sortable: true,
      render: (row) => <span className="text-sm font-semibold text-slate-800">₹{row.basePrice}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <StatusBadge
          status={row.status === 'active' ? 'success' : 'neutral'}
          label={row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        />
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="w-8 h-8 text-slate-500 hover:text-slate-900 hover:bg-slate-100">
            <Edit className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="w-8 h-8 text-slate-500 hover:text-rose-600 hover:bg-rose-50">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  const rateColumns = [
    {
      key: 'client',
      header: 'Client',
      render: (row) => <span className="text-sm font-medium text-slate-800">{row.client?.name}</span>,
    },
    {
      key: 'product',
      header: 'Product / Verification Type',
      render: (row) => (
        <div>
          <span className="text-sm text-slate-800 font-semibold block">{row.product?.name}</span>
          <span className="text-xs text-slate-550 block">{row.verificationType?.name || 'Residence Verification'}</span>
        </div>
      ),
    },
    {
      key: 'region',
      header: 'State / City',
      render: (row) => (
        <div>
          <span className="text-xs text-slate-700 font-medium block">{row.state || 'All States'}</span>
          <span className="text-[10px] text-slate-500 block">{row.city || 'All Cities'}</span>
        </div>
      ),
    },
    {
      key: 'clientRate',
      header: 'Client Rate',
      sortable: true,
      render: (row) => (
        <span className="text-sm font-bold text-emerald-600">₹{row.clientRate}</span>
      ),
    },
    {
      key: 'vendorPayout',
      header: 'Vendor Payout',
      render: (row) => (
        <div>
          <span className="text-sm font-semibold text-slate-800">₹{row.vendorPayout}</span>
          <span className="block text-[10px] font-semibold text-sky-605 mt-0.5">
            Margin: {row.clientRate > 0 ? (((row.clientRate - row.vendorPayout) / row.clientRate) * 100).toFixed(0) : 0}%
          </span>
        </div>
      ),
    },
    {
      key: 'fePayout',
      header: 'FE Payout',
      render: (row) => (
        <div>
          <span className="text-sm font-semibold text-slate-800">₹{row.fePayout}</span>
          <span className="block text-[10px] font-semibold text-purple-605 mt-0.5">
            Margin: {row.clientRate > 0 ? (((row.clientRate - row.fePayout) / row.clientRate) * 100).toFixed(0) : 0}%
          </span>
        </div>
      ),
    },
    {
      key: 'bpPayout',
      header: 'BP Payout',
      render: (row) => (
        <div>
          <span className="text-sm font-semibold text-slate-800">₹{row.bpPayout}</span>
          <span className="block text-[10px] font-semibold text-pink-605 mt-0.5">
            Margin: {row.clientRate > 0 ? (((row.clientRate - row.bpPayout) / row.clientRate) * 100).toFixed(0) : 0}%
          </span>
        </div>
      ),
    },
    {
      key: 'extraKmRate',
      header: 'Extra KM Rate',
      render: (row) => (
        <div className="flex items-center gap-1 text-sm text-slate-600">
          <Car className="w-3.5 h-3.5 text-slate-500" />
          ₹{row.extraKmRate}/km
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <Button variant="ghost" size="icon" className="w-8 h-8 text-slate-500 hover:text-slate-900 hover:bg-slate-100">
          <Edit className="w-4 h-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="product-rates-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Products & Matrix Rates</h1>
          <p className="text-sm text-slate-500 mt-1">Configure client products, FE/Vendor/BP pay scales, and city matrix rates</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex items-center justify-between bg-slate-100 p-2 rounded-xl border border-slate-200">
          <TabsList className="bg-slate-200/60 border border-slate-300 p-0.5 rounded-lg">
            <TabsTrigger value="products" className="data-[state=active]:bg-white data-[state=active]:text-blue-600 rounded-md text-slate-605 px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-all">
              <Package className="w-4 h-4" />
              Products List
            </TabsTrigger>
            <TabsTrigger value="rates" className="data-[state=active]:bg-white data-[state=active]:text-blue-600 rounded-md text-slate-605 px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-all">
              <IndianRupee className="w-4 h-4" />
              Matrix Rate Configurations
            </TabsTrigger>
          </TabsList>

          {activeTab === 'products' ? (
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-2 shadow-sm"
              onClick={() => setShowNewProductDialog(true)}
              data-testid="add-product-btn"
            >
              <Plus className="w-4 h-4" />
              Add Product
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-semibold gap-2"
                onClick={() => setShowBulkImportDialog(true)}
              >
                <Upload className="w-4 h-4" />
                Bulk Import Rates
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-2 shadow-sm"
                onClick={() => setShowNewRateDialog(true)}
                data-testid="add-rate-btn"
              >
                <Plus className="w-4 h-4" />
                Configure Payout Matrix
              </Button>
            </div>
          )}
        </div>

        <TabsContent value="products" className="mt-6">
          {/* Product Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="crm-card border border-slate-200 bg-white p-4 hover-lift shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{productsList.length}</p>
                  <p className="text-xs text-slate-500 font-medium">Total Available Products</p>
                </div>
              </div>
            </div>
            <div className="crm-card border border-slate-200 bg-white p-4 hover-lift shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                  <Check className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{productsList.filter(p => p.status === 'active').length}</p>
                  <p className="text-xs text-slate-500 font-medium">Active Verification Channels</p>
                </div>
              </div>
            </div>
            <div className="crm-card border border-slate-200 bg-white p-4 hover-lift shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">
                    ₹{Math.round(productsList.reduce((sum, p) => sum + p.basePrice, 0) / productsList.length)}
                  </p>
                  <p className="text-xs text-slate-500 font-medium">Average Base Price</p>
                </div>
              </div>
            </div>
          </div>

          <DataTable
            columns={productColumns}
            data={productsList}
            searchPlaceholder="Search product parameters..."
            selectable={false}
          />
        </TabsContent>

        <TabsContent value="rates" className="mt-6 space-y-6">
          {/* Advanced Multi-Select Matrix Filter Panel */}
          <div className="crm-card p-4 border border-slate-200 bg-white shadow-sm space-y-3">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
              <div className="flex items-center gap-2 text-slate-800 font-semibold text-sm">
                <Filter className="w-4 h-4 text-blue-600" />
                Multi-Dimensional Matrix Filter Panel
              </div>
              <button 
                onClick={() => {
                  setFilterProduct('all');
                  setFilterType('all');
                  setFilterClient('all');
                  setFilterState('all');
                  setFilterCity('all');
                }}
                className="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors"
              >
                Clear Matrix Filters
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-505 font-semibold">Client Partner</Label>
                <Select value={filterClient} onValueChange={setFilterClient}>
                  <SelectTrigger className="w-full bg-white border-slate-200 text-slate-800 text-xs">
                    <SelectValue placeholder="All Clients" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-800">
                    <SelectItem value="all">All Clients</SelectItem>
                    {clientsList.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-slate-505 font-semibold">Core Loan Product</Label>
                <Select value={filterProduct} onValueChange={setFilterProduct}>
                  <SelectTrigger className="w-full bg-white border-slate-200 text-slate-800 text-xs">
                    <SelectValue placeholder="All Products" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-800">
                    <SelectItem value="all">All Products</SelectItem>
                    {productsList.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-slate-505 font-semibold">Verification Type</Label>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-full bg-white border-slate-200 text-slate-800 text-xs">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-800">
                    <SelectItem value="all">All Types</SelectItem>
                    {defaultVerificationTypes.map((vt) => (
                      <SelectItem key={vt.id} value={vt.id}>{vt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-slate-505 font-semibold">State Matrix</Label>
                <Select value={filterState} onValueChange={setFilterState}>
                  <SelectTrigger className="w-full bg-white border-slate-200 text-slate-800 text-xs">
                    <SelectValue placeholder="All States" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-800">
                    <SelectItem value="all">All States</SelectItem>
                    {indianStates.filter(s => s !== 'All States').map((st) => (
                      <SelectItem key={st} value={st}>{st}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-slate-505 font-semibold">City Cluster</Label>
                <Select value={filterCity} onValueChange={setFilterCity}>
                  <SelectTrigger className="w-full bg-white border-slate-200 text-slate-800 text-xs">
                    <SelectValue placeholder="All Cities" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-800">
                    <SelectItem value="all">All Cities</SelectItem>
                    {filterState !== 'all' ? (
                      citiesMap[filterState]?.filter(c => c !== 'All Cities').map((city) => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="all" disabled>Select State First</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DataTable
            columns={rateColumns}
            data={filteredRates}
            searchPlaceholder="Search across active client configurations..."
            selectable={false}
          />

          {/* Rate Change History / Audit Trail Log */}
          <div className="crm-card p-6 border border-slate-200 bg-white shadow-sm space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <History className="w-5 h-5 text-indigo-600 animate-pulse" />
                  Rate Card Adjustments & Audit Trail Log
                </h3>
                <p className="text-xs text-slate-550 mt-1">Tracks history logs of modifications made to product margins and payout configurations</p>
              </div>
              <div className="relative w-full md:w-72">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Filter logs by Admin or action..."
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  className="bg-white border-slate-200 text-slate-800 text-xs pl-9 h-9"
                />
              </div>
            </div>

            <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
              {filteredHistory.map((log) => (
                <div key={log.id} className="flex gap-4 p-3 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-indigo-655" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-slate-800">
                        {log.user} <span className="text-slate-500 font-normal">({log.role})</span>
                      </p>
                      <span className="text-[10px] text-slate-500">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-xs text-indigo-600 font-medium mt-0.5">{log.action}</p>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">{log.details}</p>
                  </div>
                </div>
              ))}
              {filteredHistory.length === 0 && (
                <div className="text-center py-6 text-slate-500 text-xs flex flex-col items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-slate-600" />
                  No audit logs match your search.
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* New Product Dialog */}
      <Dialog open={showNewProductDialog} onOpenChange={setShowNewProductDialog}>
        <DialogContent className="bg-white border border-slate-200 max-w-lg text-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-900 text-lg">Add New Product</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              Introduce a new financial loan product channel into the CRM system.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="productName" className="text-slate-700 font-medium">Product Name</Label>
                <Input 
                  id="productName" 
                  value={newProductForm.name}
                  onChange={e => setNewProductForm({...newProductForm, name: e.target.value})}
                  className="bg-white border-slate-200 text-slate-800" 
                  placeholder="e.g., Solar Loan Post" 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="productCode" className="text-slate-700 font-medium">Product Code</Label>
                <Input 
                  id="productCode" 
                  value={newProductForm.code}
                  onChange={e => setNewProductForm({...newProductForm, code: e.target.value})}
                  className="bg-white border-slate-200 text-slate-800" 
                  placeholder="e.g., SLPOST" 
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description" className="text-slate-700 font-medium">Description</Label>
              <Input 
                id="description" 
                value={newProductForm.description}
                onChange={e => setNewProductForm({...newProductForm, description: e.target.value})}
                className="bg-white border-slate-200 text-slate-800" 
                placeholder="Details of verification requirements" 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="basePrice" className="text-slate-700 font-medium">Base Price (₹)</Label>
              <Input 
                id="basePrice" 
                type="number" 
                value={newProductForm.basePrice}
                onChange={e => setNewProductForm({...newProductForm, basePrice: e.target.value})}
                className="bg-white border-slate-200 text-slate-800" 
                placeholder="250" 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewProductDialog(false)} className="border-slate-200 bg-transparent text-slate-650 hover:text-slate-900 hover:bg-slate-50">
              Cancel
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold" onClick={handleCreateProduct}>
              Create Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Rate Dialog */}
      <Dialog open={showNewRateDialog} onOpenChange={setShowNewRateDialog}>
        <DialogContent className="bg-white border border-slate-200 max-w-xl text-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-900 text-lg">Configure Payout Matrix</DialogTitle>
            <DialogDescription className="text-slate-505 text-xs">
              Establish a pricing and margin model for specific client product codes and geographical clusters.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-3 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-slate-700 font-semibold">Client Partner</Label>
                <Select value={newRateForm.clientId} onValueChange={val => setNewRateForm({...newRateForm, clientId: val})}>
                  <SelectTrigger className="bg-white border-slate-200 text-slate-800 text-xs">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-805">
                    {clientsList.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-700 font-semibold">Core Loan Product</Label>
                <Select value={newRateForm.productId} onValueChange={val => setNewRateForm({...newRateForm, productId: val})}>
                  <SelectTrigger className="bg-white border-slate-200 text-slate-800 text-xs">
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-805">
                    {productsList.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1 col-span-3">
                <Label className="text-xs text-slate-700 font-semibold">Verification Type Map</Label>
                <Select value={newRateForm.verificationTypeId} onValueChange={val => setNewRateForm({...newRateForm, verificationTypeId: val})}>
                  <SelectTrigger className="bg-white border-slate-200 text-slate-800 text-xs">
                    <SelectValue placeholder="Select verification checklist type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-805">
                    {defaultVerificationTypes.map((vt) => (
                      <SelectItem key={vt.id} value={vt.id}>{vt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-slate-700 font-semibold">State Assignment</Label>
                <Select value={newRateForm.state} onValueChange={val => setNewRateForm({...newRateForm, state: val, city: 'All Cities'})}>
                  <SelectTrigger className="bg-white border-slate-200 text-slate-800 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-805">
                    {indianStates.map((st) => (
                      <SelectItem key={st} value={st}>{st}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-700 font-semibold">City Target</Label>
                <Select value={newRateForm.city} onValueChange={val => setNewRateForm({...newRateForm, city: val})}>
                  <SelectTrigger className="bg-white border-slate-200 text-slate-800 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-805">
                    {citiesMap[newRateForm.state]?.map((ct) => (
                      <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-3 mt-1">
              <span className="text-xs font-semibold text-indigo-605 block mb-3">Slab Rates & Partner Margins (₹)</span>
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="clientRate" className="text-[10px] text-slate-505 font-bold uppercase">Client Billing</Label>
                  <Input 
                    id="clientRate" 
                    type="number" 
                    value={newRateForm.clientRate}
                    onChange={e => setNewRateForm({...newRateForm, clientRate: e.target.value})}
                    className="bg-white border-slate-200 text-slate-800 text-xs" 
                    placeholder="300" 
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="vendorPayout" className="text-[10px] text-slate-505 font-bold uppercase">Vendor Payout</Label>
                  <Input 
                    id="vendorPayout" 
                    type="number" 
                    value={newRateForm.vendorPayout}
                    onChange={e => setNewRateForm({...newRateForm, vendorPayout: e.target.value})}
                    className="bg-white border-slate-200 text-slate-800 text-xs" 
                    placeholder="180" 
                  />
                  {newRateForm.clientRate && newRateForm.vendorPayout && (
                    <p className="text-[9px] font-bold text-sky-655 mt-1">
                      Margin: {(((newRateForm.clientRate - newRateForm.vendorPayout) / newRateForm.clientRate) * 100).toFixed(0)}%
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="fePayout" className="text-[10px] text-slate-505 font-bold uppercase">FE Payout</Label>
                  <Input 
                    id="fePayout" 
                    type="number" 
                    value={newRateForm.fePayout}
                    onChange={e => setNewRateForm({...newRateForm, fePayout: e.target.value})}
                    className="bg-white border-slate-200 text-slate-800 text-xs" 
                    placeholder="150" 
                  />
                  {newRateForm.clientRate && newRateForm.fePayout && (
                    <p className="text-[9px] font-bold text-purple-655 mt-1">
                      Margin: {(((newRateForm.clientRate - newRateForm.fePayout) / newRateForm.clientRate) * 100).toFixed(0)}%
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="bpPayout" className="text-[10px] text-slate-505 font-bold uppercase">BP Payout</Label>
                  <Input 
                    id="bpPayout" 
                    type="number" 
                    value={newRateForm.bpPayout}
                    onChange={e => setNewRateForm({...newRateForm, bpPayout: e.target.value})}
                    className="bg-white border-slate-200 text-slate-800 text-xs" 
                    placeholder="135" 
                  />
                  {newRateForm.clientRate && newRateForm.bpPayout && (
                    <p className="text-[9px] font-bold text-pink-655 mt-1">
                      Margin: {(((newRateForm.clientRate - newRateForm.bpPayout) / newRateForm.clientRate) * 100).toFixed(0)}%
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-1 border-t border-slate-200 pt-3">
              <Label htmlFor="extraKmRate" className="text-xs text-slate-700 font-semibold">Out-station Extra KM Rate (₹/km)</Label>
              <Input 
                id="extraKmRate" 
                type="number" 
                value={newRateForm.extraKmRate}
                onChange={e => setNewRateForm({...newRateForm, extraKmRate: e.target.value})}
                className="bg-white border-slate-200 text-slate-800 text-xs" 
                placeholder="5" 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRateDialog(false)} className="border-slate-200 bg-transparent text-slate-650 hover:text-slate-900 hover:bg-slate-50">
              Cancel
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold" onClick={handleCreateRate}>
              Save Config Matrix
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Rates Dialog */}
      <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
        <DialogContent className="bg-white border border-slate-200 max-w-2xl text-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-900 text-lg font-bold">Bulk Import Matrix Rate Sheets</DialogTitle>
            <DialogDescription className="text-slate-505 text-xs">
              Upload an Excel (.xlsx) or CSV document mapping products, cities, and tiered partner payout columns in one step.
            </DialogDescription>
          </DialogHeader>

          {importStep === 1 ? (
            <div className="py-6 space-y-4">
              <div 
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                  dragOver ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                }`}
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
              >
                <div className="flex flex-col items-center justify-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                    <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Drag and drop rate sheet here</p>
                    <p className="text-xs text-slate-500 mt-1">Accepts Excel (.xls, .xlsx) or CSV formatted templates</p>
                  </div>
                  <div className="relative">
                    <input 
                      type="file" 
                      accept=".csv, .xls, .xlsx" 
                      onChange={handleFileSelect} 
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                      Browse Files
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-indigo-50/50 rounded-lg border border-indigo-100 p-3 flex gap-3 text-xs">
                <AlertCircle className="w-5 h-5 text-indigo-650 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <span className="font-semibold text-slate-800 block">Required Excel Columns Template Map</span>
                  <p className="text-slate-500 leading-relaxed">
                    ClientName | ProductName | VerificationType | StateName | CityCluster | ClientRate | VendorPayout | FEPayout | BPPayout | ExtraKMRate
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-3">
              <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200 text-xs">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                  <div>
                    <span className="font-semibold text-slate-800">{importedFile?.name}</span>
                    <span className="text-[10px] text-slate-500 block">{(importedFile?.size / 1024).toFixed(1)} KB • Parsed Ready</span>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 font-semibold" onClick={() => setImportStep(1)}>
                  Remove File
                </Button>
              </div>

              <span className="text-xs font-semibold text-slate-700 block">Previewing Parsed Rates Matrix Sheets (3 records)</span>
              
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="w-full text-xs text-left text-slate-650">
                  <thead className="bg-slate-100 text-slate-600 uppercase text-[9px] border-b border-slate-200">
                    <tr>
                      <th className="p-3">Client</th>
                      <th className="p-3">Product / Checklist Type</th>
                      <th className="p-3">Region</th>
                      <th className="p-3">Client Rate</th>
                      <th className="p-3">Payouts (Ven/FE/BP)</th>
                      <th className="p-3">Extra KM</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {bulkPreviewData.map((row, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="p-3 font-medium text-slate-800">{row.client}</td>
                        <td className="p-3">
                          <span className="font-semibold block text-slate-800">{row.product}</span>
                          <span className="text-[10px] text-slate-500">{row.type}</span>
                        </td>
                        <td className="p-3">
                          <span className="block text-slate-850">{row.state}</span>
                          <span className="text-[10px] text-slate-500">{row.city}</span>
                        </td>
                        <td className="p-3 text-emerald-600 font-semibold">₹{row.clientRate}</td>
                        <td className="p-3">
                          <span className="font-medium text-slate-800">₹{row.vendorPayout}</span>
                          <span className="text-slate-500 mx-1">/</span>
                          <span className="text-slate-800">₹{row.fePayout}</span>
                          <span className="text-slate-500 mx-1">/</span>
                          <span className="text-slate-800">₹{row.bpPayout}</span>
                        </td>
                        <td className="p-3">₹{row.extraKmRate}/km</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkImportDialog(false)} className="border-slate-200 bg-transparent text-slate-650 hover:text-slate-905 hover:bg-slate-50">
              Cancel
            </Button>
            {importStep === 2 && (
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" onClick={executeBulkImport}>
                Import Rates Matrix
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
